import React, { useState, useEffect } from 'react';
import './CustomerList.css';
import { useEffectOnce } from '../../globalUtils/globalHooks';
import { cloneDeep } from 'lodash';

interface Customer {
    id: string;
    name: string;
    avgPercentage: number;
    isSelected: boolean;
    seriesIndex: number; // Index in the Highcharts series array
    color: string; // This will come from Highcharts series color
}

type SortOrder = 'asc' | 'desc';
type SortField = 'name' | 'avgPercentage';

interface CustomerListProps {
    customers?: Customer[];
    searchText?: string | null
    onSelectAllChange?: (value:boolean) => void;
    onSortChange?: (field: SortField, order: SortOrder) => void;
    onCheckboxChange?: (seriesIndex: number, isSelected: boolean) => void; // For Highcharts integration
}

const CustomerList: React.FC<CustomerListProps> = ({
    customers: propCustomers,
    searchText,
    onSelectAllChange,
    onSortChange,
    onCheckboxChange
}) => {

    const [customers, setCustomers] = useState<Customer[]>(propCustomers || []);
    const [filteredCustomers,setFilterCustomers] = useState<Customer[]>(propCustomers || []);
    const [selectedAll, setSelectedAll] = useState<boolean>(false);
    const [someSelected,setSomeSelected] = useState<boolean>(false);
    const [sortField, setSortField] = useState<SortField>('name');
    const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
    const [showSortDropdown, setShowSortDropdown] = useState(false);

    // Simulate fetching data from server
    useEffect(() => {
        if (!propCustomers) {
            setCustomers([]);
        } else {
            setCustomers(propCustomers);
        }
    }, [propCustomers]);

    useEffectOnce(()=>{
        if(customers.every(c=>c.isSelected) && onSelectAllChange){
            setSelectedAll(true);
            setSomeSelected(true);
            onSelectAllChange(true);
        }else if(customers.some(c=>c.isSelected)){
            setSomeSelected(true);
            setSelectedAll(false);
        }else {
            setSomeSelected(false);
            setSelectedAll(false);
        }
    })

    useEffect(()=>{
        if(customers){
            if(searchText && searchText.trim()!==''){
            setFilterCustomers(customers.filter(customer=>customer.name.toLowerCase().includes(searchText.toLowerCase())))
            }
            else{
             setFilterCustomers(customers);
            }
        }
        
    },[searchText,customers])


    useEffect(()=>{
        if(!someSelected && onSelectAllChange){
            onSelectAllChange(false);
        }
    },[someSelected])

    useEffect(()=>{
        if(customers.every(c=>c.isSelected) && onSelectAllChange){
            setSelectedAll(true);
            onSelectAllChange(true);
            setSomeSelected(true);
        }else setSelectedAll(false);
    },[customers])


    // Sort customers based on current sort settings
    const sortedCustomers = [...filteredCustomers].sort((a, b) => {
        let comparison = 0;

        if (sortField === 'name') {
            comparison = a.name.localeCompare(b.name);
        } else if (sortField === 'avgPercentage') {
            comparison = a.avgPercentage - b.avgPercentage;
        }

        return sortOrder === 'asc' ? comparison : -comparison;
    });

    const handleSort = (field: SortField) => {
        const newOrder = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
        setSortField(field);
        setSortOrder(newOrder);
        setShowSortDropdown(false);

        if (onSortChange) {
            onSortChange(field, newOrder);
        }
    };

    const handleCheckboxChange = (customerId: string) => {
        const customer = customers.find(c => c.id === customerId);
        const newSelectedState = !customer?.isSelected;
        const clonedCustomers = cloneDeep(customers)

        const newCustomersData = clonedCustomers.map(c =>
                c.id === customerId
                    ? { ...c, isSelected: newSelectedState }
                    : c
            )

        setCustomers(newCustomersData);

        if(newCustomersData.every(c=>!c.isSelected)){
            setSomeSelected(false);
            return
        }

        if (customer && onCheckboxChange) {
            onCheckboxChange(customer.seriesIndex as number, newSelectedState);
        } 
    };

const handleSelectAll = () => {
    const newSelectedState = !selectedAll;

    setCustomers(prevCustomers =>
        prevCustomers.map(c => ({ ...c, isSelected: newSelectedState }))
    );

    if (onSelectAllChange) {
        onSelectAllChange(newSelectedState);
    }

    setSelectedAll(newSelectedState);
};


    return (
        <div className="customer-list-container">
            {/* Header */}
            <div className="customer-list-header">
                <div className="sort-dropdown-wrapper">
                    <button
                        onClick={() => setShowSortDropdown(!showSortDropdown)}
                        className={`sort-button ${showSortDropdown ? 'active' : ''}`}
                    >
                        <span>Customer</span>
                        <svg
                            className={`sort-arrow ${showSortDropdown ? 'rotated' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>

                    {/* Sort Dropdown */}
                    {showSortDropdown && (
                        <div className="sort-dropdown">
                            <button
                                onClick={() => handleSort('name')}
                                className="sort-option"
                            >
                                <span>Sort by Name</span>
                                {sortField === 'name' && (
                                    <span className="sort-indicator">
                                        {sortOrder === 'asc' ? '↑' : '↓'}
                                    </span>
                                )}
                            </button>
                            <button
                                onClick={() => handleSort('avgPercentage')}
                                className="sort-option"
                            >
                                <span>Sort by Percentage</span>
                                {sortField === 'avgPercentage' && (
                                    <span className="sort-indicator">
                                        {sortOrder === 'asc' ? '↑' : '↓'}
                                    </span>
                                )}
                            </button>
                        </div>
                    )}
                </div>

                <div className="checkbox-header">
                <div className="avg-label">
                    <span>Avg %</span>
                </div>
                <div className="select-all-checkbox">
                    <div className="checkbox-wrapper">
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                checked={selectedAll}
                                onChange={() => handleSelectAll()}
                                className="checkbox-input"
                            />
                            <div className={`checkbox-custom ${selectedAll ? 'checked' : ''}`}>
                                {selectedAll && (
                                    <svg className="checkbox-checkmark" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                )}
                            </div>
                        </label>
                    </div>
                </div>
                </div>
                
            </div>

            {/* Customer List */}
            <div className="customer-list">
                {sortedCustomers.map((customer) => (
                    <div key={customer.id} className="customer-row">
                        {/* Customer Name with Color Indicator */}
                        <div className="customer-info">
                            <div
                                className="color-indicator"
                                style={{ backgroundColor: customer.color }}
                            ></div>
                            <span className="customer-name" title={customer.name}>
                                {customer.name}
                            </span>
                        </div>

                        <div className='customer-row-data'>
                        {/* Percentage */}
                        <div className="customer-percentage">
                            {customer.avgPercentage.toFixed(2)}%
                        </div>

                        {/* Checkbox */}
                        <div className="checkbox-wrapper">
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={customer.isSelected}
                                    onChange={() => handleCheckboxChange(customer.id)}
                                    className="checkbox-input"
                                />
                                <div className={`checkbox-custom ${customer.isSelected ? 'checked' : ''}`}>
                                    {customer.isSelected && (
                                        <svg className="checkbox-checkmark" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                    )}
                                </div>
                            </label>
                        </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default CustomerList;